import React, { useState, useEffect, useMemo } from "react";
import { Link } from "react-router-dom";
import { MessageCircle, CheckCircle2, Search, PackageSearch, ExternalLink } from "lucide-react";
import { wa, logClick } from "../mock";
import { ProductPhotos } from "./ProductPhotos";
import { STOCK_CONDITION_NOTE, specificCondition } from "../lib/condition";

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;
const inr = (n) => "\u20b9" + Number(n || 0).toLocaleString("en-IN");

export default function Gallery() {
  const [all, setAll] = useState([]);
  const [cat, setCat] = useState("All");
  const [q, setQ] = useState("");
  const [limit, setLimit] = useState(12);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    fetch(`${API}/products`)
      .then((r) => r.json())
      .then((d) => setAll(Array.isArray(d) ? d : []))
      .catch(() => setAll([]))
      .finally(() => setLoading(false));
  }, []);

  const cats = useMemo(
    () => ["All", ...Array.from(new Set(all.map((p) => p.category)))],
    [all]
  );

  const list = useMemo(() => {
    const term = q.trim().toLowerCase();
    return all.filter((p) => {
      const okCat = cat === "All" || p.category === cat;
      const okQ = !term || `${p.name} ${p.brand} ${p.category}`.toLowerCase().includes(term);
      return okCat && okQ;
    });
  }, [all, cat, q]);

  const shown = list.slice(0, limit);

  return (
    <section id="stock" data-testid="inventory-section" className="home-section" style={{ scrollMarginTop: 120, background: "var(--paper)" }}>
      <div className="container">
        <div className="reveal mx-auto max-w-[820px] text-center">
          <p className="eyebrow centered">Available pre-owned stock</p>
          <h2 className="mt-3 font-heading font-extrabold leading-[1.08]"
              style={{ color: "var(--green-950)", fontSize: "clamp(28px,3.6vw,42px)" }}>
            Live inventory
          </h2>
          <p className="mt-4 text-[16px]" style={{ color: "var(--muted)" }}>
            {STOCK_CONDITION_NOTE} Send a quick WhatsApp enquiry on
            any item to confirm current availability and best price.
          </p>
        </div>

        {/* Search + filters */}
        <div className="reveal mx-auto mt-8 flex max-w-[520px] items-center gap-2 rounded-full border bg-white px-4 py-2.5"
             style={{ borderColor: "var(--line)", boxShadow: "0 8px 20px rgba(45,106,79,0.05)" }}>
          <Search size={18} style={{ color: "var(--muted)" }} />
          <input value={q} onChange={(e) => { setQ(e.target.value); setLimit(12); }} data-testid="inventory-search"
                 placeholder="Search laptops, brands, specs…"
                 className="w-full bg-transparent text-[15px] outline-none" style={{ color: "var(--text)" }} />
        </div>

        <div className="reveal mt-5 flex flex-wrap items-center justify-center gap-2">
          {cats.map((c) => (
            <button key={c} onClick={() => { setCat(c); setLimit(12); }} data-testid={`inventory-filter-${c.toLowerCase()}`}
                    className="kp-button kp-button--compact rounded-full border px-4 py-2 font-heading text-[13.5px] font-bold transition-colors"
                    style={c === cat
                      ? { background: "var(--bright-green-strong)", color: "#fff", borderColor: "var(--bright-green-strong)" }
                      : { background: "#fff", color: "var(--green-950)", borderColor: "var(--line)" }}>
              {c}
            </button>
          ))}
        </div>

        {cat !== "All" && (
          <div className="mt-4 text-center">
            <Link to={`/stock/${encodeURIComponent(cat)}`}
                  className="inline-flex items-center gap-1.5 font-heading text-[13.5px] font-bold" style={{ color: "var(--orange-700)" }}>
              Open shareable {cat} page <ExternalLink size={14} />
            </Link>
          </div>
        )}

        {loading ? (
          <p className="mt-10 text-center" style={{ color: "var(--muted)" }}>Loading inventory…</p>
        ) : shown.length === 0 ? (
          <div className="mt-10 grid place-items-center gap-3 py-16 text-center">
            <PackageSearch size={40} style={{ color: "var(--muted)" }} />
            <p className="font-heading font-bold" style={{ color: "var(--green-950)" }}>No items match your search</p>
          </div>
        ) : (
          <>
            <div className="mt-9 grid gap-5 sm:grid-cols-2 lg:grid-cols-4">
              {shown.map((p) => (
                <article key={p.id} className="group flex flex-col overflow-hidden rounded-2xl border bg-white transition-transform hover:-translate-y-1.5"
                         style={{ borderColor: "var(--line)", boxShadow: "0 12px 30px rgba(45,106,79,0.08)" }}>
                  <div className="relative h-40 overflow-hidden" style={{ background: "var(--green-100)" }}>
                    <ProductPhotos p={p} />
                    <span className="absolute left-3 top-3 rounded-full px-3 py-1 font-heading text-[11.5px] font-bold text-white"
                          style={{ background: "var(--bright-green-strong)" }}>{p.category}</span>
                    {p.sold ? (
                      <span className="absolute right-3 top-3 rounded-full px-3 py-1 font-heading text-[11px] font-extrabold text-white"
                            style={{ background: "var(--orange-700)" }}>SOLD</span>
                    ) : p.qty > 1 ? (
                      <span className="absolute right-3 top-3 rounded-full px-2.5 py-1 font-heading text-[11px] font-bold"
                            style={{ background: "var(--yellow-500)", color: "var(--green-950)" }}>{p.qty} in stock</span>
                    ) : null}
                  </div>
                  <div className="flex flex-1 flex-col p-4">
                    <span className="text-[13px] font-extrabold uppercase tracking-wide" style={{ color: "var(--orange-800)" }}>{p.brand}</span>
                    <h3 className="mt-1 font-sans text-[15px] font-medium leading-relaxed line-clamp-3" style={{ color: "var(--ink)" }}>
                      <Link to={`/product/${p.id}`} data-testid={`product-link-${p.id}`} className="hover:underline">{p.name}</Link>
                    </h3>
                    {specificCondition(p) && (
                      <span data-testid={`gallery-condition-${p.id}`} className="mt-3 inline-flex w-max items-center gap-1 text-[12px] font-bold" style={{ color: "var(--bright-green-strong)" }}>
                        <CheckCircle2 size={13} /> {specificCondition(p)}
                      </span>
                    )}
                    <div className="mt-auto flex items-center justify-between pt-3">
                      {p.price_on_request ? (
                        <span className="font-heading text-[15px] font-extrabold" style={{ color: "var(--green-800)" }}>Price on request</span>
                      ) : (
                        <span className="font-heading text-[18px] font-extrabold" style={{ color: "var(--orange-700)" }}>{inr(p.price)}</span>
                      )}
                    </div>
                    <a href={wa(`Hi Kapatia, I'm interested in: ${p.name} (${p.brand}). Is it available?`)}
                       onClick={() => logClick(p, "gallery")}
                       target="_blank" rel="noopener noreferrer" data-testid={`inventory-enquire-${p.id}`}
                       className="kp-button kp-button--compact mt-3 inline-flex items-center justify-center gap-2 rounded-full px-4 py-2.5 font-heading text-[13.5px] font-bold text-white transition-transform hover:-translate-y-0.5"
                       style={{ background: "linear-gradient(135deg, var(--bright-green), var(--bright-green-strong))" }}>
                      <MessageCircle size={15} /> Enquire
                    </a>
                  </div>
                </article>
              ))}
            </div>
            {shown.length < list.length && (
              <div className="mt-9 text-center">
                <button onClick={() => setLimit((l) => l + 12)} data-testid="inventory-show-more"
                        className="kp-button rounded-full border-2 px-7 py-3 font-heading font-bold transition-transform hover:-translate-y-0.5"
                        style={{ borderColor: "var(--green-800)", color: "var(--green-900)" }}>
                  Show more ({list.length - shown.length} more)
                </button>
              </div>
            )}
          </>
        )}
      </div>
    </section>
  );
}
